import * as React from 'react';
import styles from './styles.module.css';
import ZoneHeroCard from './ZoneHeroCard';
import { Card, Divider, Typography } from '@mui/material';
import { useLanguageLocale } from '../../../hooks/language-locale-hooks';
import { DeviceData, ZoneDetail } from '../../../types/device';
import { useSelector } from 'react-redux';
import { Selectors } from '../../../selectors';
import { useEffect, useState, useCallback } from 'react';
import { LuminaryList } from '../../../types/luminary';
import { useToasts } from '../../../data/DataProviders/ToastProvider';
import { DropDownListComponent } from '../../../components/DropDownList/DropDownListComponent';
import { DEVICE_TYPE_WIRELESS, getDeviceType, getLuminaryBitStatusState, LUMINARY_BIT_STATUS, luminaryBitStatusEnum, luminaryBitStatusWirelessEnum } from '../../enum/ENUM';
interface Props {
    systemDetail: DeviceData;
    setSelectedZones?: (zone: number[]) => void;
    setSelectedStatuses?: (status: string[]) => void;
}

const ZoneList: React.FunctionComponent<Props> = ({ systemDetail, setSelectedZones = (zones: number[]) => { }, setSelectedStatuses }) => {
    const asset = useSelector(Selectors.Dashboard.asset);
    const { t } = useLanguageLocale();
    const { addToast } = useToasts();
    const [selectedZone, setSelectedZone] = React.useState<ZoneDetail | undefined>(undefined);
    const zones = systemDetail.zoneDetails || [];
    const [luminaryList, setLuminaryList] = useState<LuminaryList>([]);
    const [lumnsCount, setLumnsCount] = useState(0)
    const [selectedStatus, setSelectedStatus] = React.useState<string | undefined>(undefined);


    const zoneListClassName = `${styles.zoneList} ${selectedZone ? styles.hasSelectedZone : ''}`;


    const handleZoneSelectImmediate = (zone: ZoneDetail) => {
        if (selectedZone?.id === zone.id) {
            setSelectedZone(undefined);
        } else {
            setSelectedZone(zone);
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const handleZoneSelect = useCallback((() => {
        let timeoutId: NodeJS.Timeout | null = null;
        return (zone: ZoneDetail) => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
            timeoutId = setTimeout(() => {
                handleZoneSelectImmediate(zone);
                timeoutId = null;
            }, 1000);
        };
    })(), [selectedZone]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (selectedZone) {
            setSelectedZones([selectedZone.id])
        } else {
            setSelectedZones([]);
        }
    }, [selectedZone]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!asset.luminaryListLoading && asset.luminaryListSuccess) {
            setLuminaryList(asset.luminaryList.detail);
            setLumnsCount(asset.luminaryList.totalLuminaireCount ?? 0)
        } else if (!asset.luminaryListLoading && asset.luminaryListFail) {
            addToast(t('TOAST_MSG.TOAST_ERROR_LUMNS_FAILS'), 'error');
            setLuminaryList([]);
        }
    }, [asset.luminaryListLoading, asset.luminaryListSuccess, asset.luminaryListFail]); // eslint-disable-line react-hooks/exhaustive-deps

    const zoneItemList = zones.map(zone => ({
        id: zone.id.toString(), // ItemData expects id as string
        name: zone.name,
    }));

    const handleZoneChange = (value: any) => {
        if (value === '') {
            setSelectedZone(undefined);
        } else {
            setSelectedZone(zones.find((record: any) => record.id === Number(value)));
        }
    }

    const handleSelectedStatuses = (status: string | undefined) => {
        setSelectedStatus(status);
        if (status) {
            setSelectedStatuses?.([status]);
        } else {
            setSelectedStatuses?.([]);
        }
    }

    const getLuminaryBitStatusCount = (status: number): number => {
    switch (status) {
        case LUMINARY_BIT_STATUS.STATUS_BLOCKED:
            return asset.luminaryList.totalBlockedCount || 0;
        case LUMINARY_BIT_STATUS.STATUS_WARNING:
            return asset.luminaryList.totalWarningCount || 0;
        case LUMINARY_BIT_STATUS.STATUS_IN_TEST:
            return asset.luminaryList.totalTestCount || 0;
        case LUMINARY_BIT_STATUS.STATUS_EMERGENCY:
            return asset.luminaryList.totalEmergencyCount || 0;
        case LUMINARY_BIT_STATUS.STATUS_READY:
            return asset.luminaryList.totalReadyCount || 0;
        case LUMINARY_BIT_STATUS.STATUS_COMMUNICATION_FAILURE:
            return asset.luminaryList.totalCommunicationFailure || 0;
        case LUMINARY_BIT_STATUS.STATUS_BATTERY_FAILURE:
            return asset.luminaryList.totalBatteryFailure || 0;
        case LUMINARY_BIT_STATUS.STATUS_LAMP_FAILURE:
            return asset.luminaryList.totalLampFailure || 0;
        case LUMINARY_BIT_STATUS.STATUS_CHARGE_FAILURE:
            return asset.luminaryList.totalChargeFailure || 0;
        case LUMINARY_BIT_STATUS.STATUS_FT_FAILURE:
            return asset.luminaryList.totalFT_Failure || 0;
        case LUMINARY_BIT_STATUS.STATUS_DT_FAILURE:
            return asset.luminaryList.totalDT_Failure || 0;
        case LUMINARY_BIT_STATUS.STATUS_CIRCUIT_FAILURE:
            return asset.luminaryList.totalCircuit_Failure || 0;
        default:
            return asset.luminaryList.totalReadyCount || 0;
    }
};

    return (
        <Card style={{
            width: '100%',
            marginBottom: '1.328vh',

        }} data-testid="zone-list-container">
            <div style={{ display: 'flex', placeContent: 'center', position: 'relative', justifyContent: 'flex-start', gap: '7%' }}>
                {zones.length === 0 && (<div className={styles.lineList}>
                    <div className={styles.noDataFound}>{t('NETWORK.NO_DATA_FOUND')}</div>
                </div>)}
                {zones.length > 0 && <div className={styles.zoneDropDownContainer}>
                    <DropDownListComponent
                        marginTop={10}
                        value={`${t('FILTER.ALL')} ${t('DEVICE_DETAILS.ZONES')}`}
                        list={zoneItemList}
                        handleSelection={(value: any) => { handleZoneChange(Number(value)) }}
                        selected={selectedZone?.id?.toString() || ''}
                        defaultSelection={selectedZone?.id?.toString() || ''}
                    />
                    {!selectedZone && (
                        <Typography fontSize={14} fontWeight={400} marginLeft={1}>{lumnsCount} {t('DEVICE_DETAILS.LUMINARIES_COUNTS')}</Typography>
                    )}
                    {selectedZone && (
                        <>
                            <Typography fontWeight={600} marginLeft={1}>{`"${selectedZone.name}"`}</Typography>
                            <Typography marginLeft={1}>{selectedZone.luminairesCount} {t('DEVICE_DETAILS.LUMINARIES_COUNTS')}</Typography>
                        </>
                    )}
                </div>}

                <div className={styles.lineZonesContainer} style={{ flex: 'none' }}>
                    <div className={zoneListClassName} data-testid="zone-list">
                        {
                            zones.map((zone, i) => {
                                return <ZoneHeroCard
                                    click={(zoneData) => handleZoneSelect(zoneData)}
                                    data={{
                                        id: zone.id,
                                        name: zone.name,
                                        status: zone.status,
                                        description: zone.description || t('MANAGE_USERS.NOT_APPLICABLE'),
                                        luminairesCount: zone.luminairesCount,
                                        lineId: selectedZone?.id || 0,
                                        bitStatus: zone.bitStatus,
                                    }}
                                    key={i}
                                    isSelected={zone.id === selectedZone?.id} />
                            })
                        }
                    </div>
                </div>
            </div>
            <Divider />
            <div>
                {
                    selectedZone &&
                    <>
                        <div style={{ display: 'inline-flex', alignItems: 'center', columnGap: 7, marginBottom: 10, padding: '0 16px' }}>
                            <Typography fontWeight={600}>{`"${selectedZone.name}"`}</Typography>
                            <Typography>{`Zone ${selectedZone?.id}`}</Typography>
                        </div>
                    </>
                }

                <div className={styles.chipFilterContainer} data-testid="luminary-chip-filter-container">
                    <div style={{ marginLeft: '1%' }}>
                        <div onClick={() => handleSelectedStatuses(undefined)} className={`${styles.statusChip} ${selectedStatus === undefined ? styles.active : ''}`} data-testid="line-zone-chip-filter-all">
                            <span>{t('SEARCH.ALL_STATUSES')} ({selectedZone ? selectedZone.luminairesCount : lumnsCount})</span>
                        </div>
                    </div>
                    <div className={styles.chipFilterContainerItemDevider}></div>
                    <div className={styles.statusFilterContainer}>
                        {
                            (systemDetail.systemSubType === getDeviceType(DEVICE_TYPE_WIRELESS) ? luminaryBitStatusWirelessEnum : luminaryBitStatusEnum).map((status) => {
                                return <div onClick={() => {
                                    handleSelectedStatuses(status.id);
                                }} style={{ display: 'inline-flex', alignContent: 'center', columnGap: 5 }} className={`${styles.statusChip} ${selectedStatus === status.id ? styles.active : ''}`} data-testid="line-zone-chip-filter-total-failed">
                                    <img
                                        src={getLuminaryBitStatusState(parseInt(status.id)).statusImage}
                                        alt="logo"
                                        style={{ display: 'flex', alignItems: 'right' }}
                                        id="device-status-icon"
                                        data-testid="device-status-icon"
                                    />
                                    <span>{t(getLuminaryBitStatusState(parseInt(status.id)).statusTitle)+` (${getLuminaryBitStatusCount(parseInt(status.id))})`}</span>
                                </div>
                            })
                        }
                    </div>
                </div>
            </div>
        </Card>);
};

export default ZoneList;